"use client";

import Link from "next/link";
import { useMemo, useState } from "react";

import { BUILT_GROUPS } from "@/lib/built-catalogue";

/**
 * Things we could build, presented to be read rather than completed.
 *
 * THIS BRIEFLY HAD A TICK BOX ON EVERY ROW AND A "SEND AS MY BRIEF" BAR. That
 * was a misreading of what Tom asked for and he said so plainly: nobody is
 * going to work through a hundred options ticking boxes. Asking a visitor to
 * process the whole list turns inspiration into homework, and homework gets
 * closed. Do not reintroduce it.
 *
 * What the page is for: most owners know roughly what they want and have no
 * idea what is possible, so this is a browsable menu of things they would never
 * have thought to ask for. They read a few, one lands, and they mention it when
 * they get in touch. The filter is how somebody narrows it, not a checklist.
 *
 * Presentation notes that still hold: no cards. Entries vary a lot in length,
 * and boxes either stretch to match or leave holes. Hairline rows have no
 * height to equalise.
 */

const ALL = "All";

export function BuiltCatalogue() {
  const [group, setGroup] = useState<string>(ALL);

  const groups = useMemo(
    () => BUILT_GROUPS.filter((g) => group === ALL || g.slug === group),
    [group],
  );

  const shown = groups.reduce((n, g) => n + g.items.length, 0);

  /* Open the first group so the page is never a wall of closed rows, and open everything when
     the reader has filtered to a single group, because narrowing to one IS asking to see it. */
  const openByDefault = (slug: string) =>
    group !== ALL || slug === BUILT_GROUPS[0]?.slug;

  return (
    <div className="mt-12 md:mt-16">
      <div className="flex flex-col gap-3 border-y border-white/[0.1] py-5 md:flex-row md:items-center md:gap-5">
        <span
          className="shrink-0 text-sm font-semibold tracking-[0.01em] text-ownly-accent-text"
          id="filter-category"
        >
          Show
        </span>
        <div className="flex flex-wrap gap-2" role="group" aria-labelledby="filter-category">
          {[ALL, ...BUILT_GROUPS.map((g) => g.slug)].map((option) => {
            const active = option === group;
            return (
              <button
                key={option}
                type="button"
                onClick={() => setGroup(option)}
                aria-pressed={active}
                className={`inline-flex h-10 items-center rounded-full border px-4 text-sm font-medium transition-[border-color,background-color,color] duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ownly-accent/50 focus-visible:ring-offset-2 focus-visible:ring-offset-ownly-bg ${
                  active
                    ? "border-ownly-accent/45 bg-ownly-accent/[0.16] text-white"
                    : "border-white/[0.12] bg-white/[0.03] text-white/62 hover:border-white/[0.22] hover:text-white/85"
                }`}
              >
                {option === ALL
                  ? "Everything"
                  : (BUILT_GROUPS.find((g) => g.slug === option)?.name ?? option)}
              </button>
            );
          })}
        </div>
      </div>

      <p
        className="mt-4 text-sm tracking-[0.01em] text-white/60"
        aria-live="polite"
      >
        Showing {shown} {shown === 1 ? "idea" : "ideas"}
      </p>

      <div className="mt-10 space-y-8 md:space-y-10">
        {groups.map((g) => (
          <section key={g.slug} id={g.slug} className="scroll-mt-28 md:scroll-mt-32">
            {/* EACH GROUP COLLAPSES. Rendered open, all 123 ideas came to 26,200px, which is
                thirty-one phone screens of scrolling to reach the end. Now the page is a list
                of groups you can open, so it is scannable in one screen and the detail is one
                tap away. The first group starts open so the page is never just a wall of
                closed rows. Filtering to a single group opens it, because someone who has
                narrowed to one group has already said what they want to see. */}
            <details open={openByDefault(g.slug)} className="group/details">
              <summary className="cursor-pointer list-none border-b border-white/[0.14] pb-5 marker:content-none">
                <div className="flex items-baseline justify-between gap-6">
                  <h2 className="ownly-display ownly-card-title text-white">
                    {g.name}
                  </h2>
                  <span className="flex shrink-0 items-center gap-3 text-sm tracking-[0.01em] text-ownly-accent-text">
                    {g.items.length} ideas
                    <span
                      className="flex size-5 items-center justify-center rounded-full border border-ownly-accent/40 bg-ownly-accent/10 text-ownly-accent-text transition-transform duration-200 group-open/details:rotate-45"
                      aria-hidden
                    >
                      <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                        <path
                          d="M5 1.25V8.75M1.25 5H8.75"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                        />
                      </svg>
                    </span>
                  </span>
                </div>
                <p className="ownly-body mt-2 leading-[27px] text-white/85">
                  {g.blurb}
                </p>
              </summary>

            <ul className="grid md:grid-cols-2 md:gap-x-10 xl:grid-cols-3 xl:gap-x-12">
              {g.items.map((item) => (
                <li
                  key={item.id}
                  id={item.id}
                  className="group scroll-mt-28 border-b border-white/[0.08] py-5"
                >
                  <h3 className="ownly-display ownly-small-title flex items-start gap-2.5 text-white">
                    <span
                      className="mt-[0.55em] size-1 shrink-0 rounded-full bg-ownly-accent/70 transition-colors duration-300 group-hover:bg-ownly-accent"
                      aria-hidden
                    />
                    {item.title}
                  </h3>
                  <p className="ownly-body mt-2 pl-[0.9rem] leading-[27px] text-white/85">
                    {item.what}
                  </p>
                </li>
              ))}
            </ul>
            <p className="mt-5">
              <Link
                href={`/built/${g.slug}`}
                className="text-body font-medium text-ownly-accent-text underline decoration-ownly-accent/35 underline-offset-2 transition-colors hover:text-[#5ca3ff]"
              >
                Read {g.name.toLowerCase()} on its own page
              </Link>
            </p>
            </details>
          </section>
        ))}
      </div>
    </div>
  );
}
